import { useMemo, useState } from "react"

import { toast } from "sonner"

import {
  useCreateLocation,
  useCreateLocationCustomer,
  useDeleteLocationCustomer,
  useUpdateLocation,
} from "@/hooks/useCollections"
import { Customer, Location } from "@/types/collections"

import { LocationFormData } from "./types"

export function useLocationDialog(customers: Customer[], refetchLocations: () => unknown) {
  const createLocation = useCreateLocation()
  const updateLocation = useUpdateLocation()
  const createLocationCustomer = useCreateLocationCustomer()
  const deleteLocationCustomer = useDeleteLocationCustomer()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Location | null>(null)
  const [form, setForm] = useState<Partial<LocationFormData>>({})
  // The customer picker's own search, separate from the directory's.
  const [customerSearchTerm, setCustomerSearchTerm] = useState("")
  const [samsaraFailed, setSamsaraFailed] = useState(false)

  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm) return customers
    const term = customerSearchTerm.toLowerCase()
    return customers.filter(
      (c) => c.name.toLowerCase().includes(term) || c.account_id.toLowerCase().includes(term),
    )
  }, [customers, customerSearchTerm])

  const openDialog = (location?: Location) => {
    setCustomerSearchTerm("")

    if (location) {
      setEditing(location)
      const customerIds =
        location.customers?.map((lc) => lc.customer_id) ||
        (location.customer_id ? [location.customer_id] : [])
      setForm({
        site_name: location.site_name,
        location_id: location.location_id,
        internal_id: location.internal_id,
        contact_name: location.contact_name,
        contact_phone: location.contact_phone,
        customer_id: location.customer_id || customerIds[0] || "", // First customer (for backwards compatibility)
        customer_ids: customerIds,
        address: location.address,
        postcode: location.postcode,
        latitude: location.latitude,
        longitude: location.longitude,
        collection_protocol: location.collection_protocol,
        opening_time: location.opening_time,
        closing_time: location.closing_time,
        loading_unit_type: location.loading_unit_type,
        parcels_per_loading_unit: location.parcels_per_loading_unit,
        status: location.status,
      })
    } else {
      setEditing(null)
      setSamsaraFailed(false)
      setForm({
        site_name: "",
        location_id: "",
        internal_id: "",
        contact_name: "",
        contact_phone: "",
        customer_id: "",
        customer_ids: [],
        address: "",
        postcode: "",
        latitude: 51.5074,
        longitude: -0.1278,
        opening_time: "",
        closing_time: "",
        loading_unit_type: undefined,
        parcels_per_loading_unit: 0,
        status: "active",
      })
    }
    setOpen(true)
  }

  const save = async () => {
    try {
      const lat = form.latitude != null ? Number(form.latitude) : null
      const lng = form.longitude != null ? Number(form.longitude) : null
      if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        toast.error("Missing coordinates", {
          description:
            "Latitude and longitude are required. Please enter them (or create in Samsara again).",
        })
        return
      }
      if (lat === 0 && lng === 0) {
        toast.error("Invalid coordinates", {
          description:
            "Latitude and longitude cannot both be 0,0. Please enter valid coordinates or create in Samsara again.",
        })
        return
      }

      const { customer_ids, ...locationData } = form
      const firstCustomerId = customer_ids?.[0] || form.customer_id
      const dataToSave = {
        ...locationData,
        customer_id: firstCustomerId,
        latitude: lat,
        longitude: lng,
        opening_time: locationData.opening_time || undefined,
        closing_time: locationData.closing_time || undefined,
        loading_unit_type: locationData.loading_unit_type || undefined,
        parcels_per_loading_unit: locationData.parcels_per_loading_unit || undefined,
      }

      let locationId: string

      if (editing) {
        await updateLocation.mutateAsync({
          id: editing.id,
          data: dataToSave,
        })
        locationId = editing.id

        const existingCustomerIds =
          editing.customers?.map((lc) => lc.customer_id) ||
          (editing.customer_id ? [editing.customer_id] : [])
        const newCustomerIds = customer_ids || []

        for (const existingId of existingCustomerIds) {
          if (!newCustomerIds.includes(existingId)) {
            const junctionRecord = editing.customers?.find(
              (lc) => lc.customer_id === existingId,
            )
            if (junctionRecord) {
              await deleteLocationCustomer.mutateAsync(junctionRecord.id)
            }
          }
        }

        for (const customerId of newCustomerIds) {
          if (!existingCustomerIds.includes(customerId)) {
            await createLocationCustomer.mutateAsync({
              location_id: locationId,
              customer_id: customerId,
              is_primary: false,
              relationship_type: "owner",
            })
          }
        }

        toast.success("Location updated successfully")
      } else {
        const result = await createLocation.mutateAsync(dataToSave as Location)
        locationId = result.id

        const customerIdsToAssociate = customer_ids || (firstCustomerId ? [firstCustomerId] : [])
        for (const customerId of customerIdsToAssociate) {
          await createLocationCustomer.mutateAsync({
            location_id: locationId,
            customer_id: customerId,
            is_primary: false,
            relationship_type: "owner",
          })
        }

        toast.success("Location created successfully")
      }
      setOpen(false)
      setCustomerSearchTerm("")
      refetchLocations()
    } catch (error) {
      toast.error("Error saving location", {
        description: error instanceof Error ? error.message : "Unknown error",
      })
    }
  }

  const isSaving = createLocation.isPending || updateLocation.isPending

  return {
    open,
    setOpen,
    editing,
    form,
    setForm,
    customerSearchTerm,
    setCustomerSearchTerm,
    filteredCustomers,
    samsaraFailed,
    setSamsaraFailed,
    openDialog,
    save,
    isSaving,
  }
}

export type LocationDialogState = ReturnType<typeof useLocationDialog>
